import React from 'react';
import {
    Navigate,
    Outlet,
    Route,
    BrowserRouter as Router,
    Routes,
    useLocation,
} from 'react-router-dom';
import { MobileDesktopRecommendation } from '@/components/common/MobileDesktopRecommendation';
import Loading from '@/components/ui/Loading';
import { useAuth } from '@/context/AuthContext';
import { useAnalyticsIdentity } from '@/hooks/use-analytics-identity';
import { capture } from '@/lib/posthog';
import {
    DEV_RECORDING_SESSION_PAGE,
    getDocumentTitle,
    matchRoutePattern,
} from '@/lib/route-patterns';
import { createPageUrl } from '@/utils';
import FeatureFlags from './admin/FeatureFlagsPage';
import Assets from './assets/AssetsPage';
import AuthConflict from './auth/AuthConflict';
import SignIn from './auth/SignIn';
import SignUp from './auth/SignUp';
import VerifyEmail from './auth/VerifyEmail';
import {
    AuthConflictPage,
    WelcomePage,
    DashboardPage,
    PatientsPage,
    PatientDetailPage,
    PatientAnamnesisPage,
    ProfilePage,
    ReportPage,
    SignInPage,
    SignUpPage,
    SubscribePage,
    VerifyEmailPage,
    PrivacyPolicyPage,
    TermsOfUsePage,
    BillingPage,
    SurveysPage,
    AnswerSurveyPage,
    SurveyPreviewPage,
    CustomSurveyBuilderPage,
    SurveyResponsePage,
    TestPreviewPage,
    TestResponsePage,
    AnswerTestPage,
    AssetsPage,
    ReportTemplatePreviewPage,
    ToolsPageRoute,
    HelpPage,
    HelpTopicPage,
    HelpArticlePage,
    SupportPage,
    SupportTicketPage,
    SupportInboxPage,
    FeatureFlagsAdminPage,
} from './constants';
import Billing from './create-report/billing/Billing';
import CreateReport from './create-report/CreateReport';
import TemplatePreviewPage from './create-report/TemplatePreviewPage';
import Dashboard from './dashboard';
import HelpArticle from './help/HelpArticlePage';
import Help from './help/HelpPage';
import HelpTopic from './help/HelpTopicPage';
import Layout from './Layout';
import Patients from './Patients';
import AnamnesisEditorPage from './patients/anamnesis/AnamnesisEditorPage';
import PatientDetails from './patients/PatientDetails';
import PrivacyPolicy from './PrivacyPolicy';
import Profile from './Profile';
import AnswerSurvey from './public/AnswerSurvey';
import AnswerTest from './public/AnswerTest';
import Subscribe from './subscribe/SubscribePage';
import SupportInbox from './support/SupportInboxPage';
import Support from './support/SupportPage';
import SupportTicket from './support/SupportTicketPage';
import CustomSurveyBuilder from './surveys/CustomSurveyBuilder';
import SurveyPreview from './surveys/SurveyPreview';
import SurveyResponseView from './surveys/SurveyResponseView';
import Surveys from './surveys/SurveysPage';
import TermsOfUse from './TermsOfUse';
import TestPreview from './tests/TestPreview';
import TestResponseView from './tests/TestResponseView';
import ToolsPage from './tools/ToolsPage';
import Welcome from './welcome/WelcomePage';

const RecordingSessionDevPage = import.meta.env.DEV
    ? React.lazy(() => import('@/__dev__/RecordingSessionPage'))
    : null;

/**
 * Owns the document title and the one and only pageview event.
 *
 * Pageviews carry the route *pattern*, never `location.pathname` — concrete
 * paths embed patient and report UUIDs. Adding a route means adding it to
 * ROUTE_METAS in `@/lib/route-patterns`; a route missing from that table is
 * reported as `/unknown` rather than leaking its real path.
 */
function PageTracker() {
    const location = useLocation();
    const lastTrackedPathname = React.useRef<string | null>(null);

    React.useEffect(() => {
        document.title = getDocumentTitle(location.pathname);
    }, [location.pathname]);

    React.useEffect(() => {
        // StrictMode invokes effects twice in development; without this the
        // same pageview is counted twice.
        if (lastTrackedPathname.current === location.pathname) return;
        lastTrackedPathname.current = location.pathname;

        capture('$pageview', { route: matchRoutePattern(location.pathname) });
    }, [location.pathname]);

    return null;
}

/** Side-effect only: binds the analytics identity to the signed-in user. */
function AnalyticsIdentity() {
    useAnalyticsIdentity();

    return null;
}

function RecordingSessionDevRoute() {
    if (!RecordingSessionDevPage) {
        return <Navigate to="/" replace />;
    }

    const Page = RecordingSessionDevPage;

    return (
        <React.Suspense fallback={<Loading />}>
            <Page />
        </React.Suspense>
    );
}

const PAGES: Record<string, React.ComponentType> = {
    [DashboardPage]: Dashboard,
    [ProfilePage]: Profile,
    [PatientsPage]: Patients,
    [ReportPage]: CreateReport,
    [BillingPage]: Billing,
    [SurveysPage]: Surveys,
    [AssetsPage]: Assets,
    [ToolsPageRoute]: ToolsPage,
    [HelpPage]: Help,
    [SupportPage]: Support,
    [FeatureFlagsAdminPage]: FeatureFlags,
};

function _getCurrentPage(url: string): string {
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }
    let urlLastPart = url.split('/').pop() || '';
    if (urlLastPart.includes('?')) {
        urlLastPart = urlLastPart.split('?')[0];
    }

    const pageName = Object.keys(PAGES).find(
        (page) => page.toLowerCase() === urlLastPart.toLowerCase(),
    );
    return pageName || Object.keys(PAGES)[0];
}

const PrivateRoutes = () => {
    const { user, status, isLoading } = useAuth();
    if (isLoading) return <Loading />;

    if (status === 'pending_verification') {
        return <Navigate to={`/${VerifyEmailPage}`} replace />;
    }

    if (status === 'blocked_conflict') {
        return <Navigate to={`/${AuthConflictPage}`} replace />;
    }

    return user ? <Outlet /> : <Navigate to={`/${SignInPage}`} replace />;
};

const PublicRoutes = () => {
    const location = useLocation();
    const { user, status, isLoading } = useAuth();

    if (isLoading) return <Loading />;

    if (status === 'pending_verification') {
        return location.pathname === `/${VerifyEmailPage}` ? (
            <Outlet />
        ) : (
            <Navigate to={`/${VerifyEmailPage}`} replace />
        );
    }

    if (status === 'blocked_conflict') {
        return location.pathname === `/${AuthConflictPage}` ? (
            <Outlet />
        ) : (
            <Navigate to={`/${AuthConflictPage}`} replace />
        );
    }

    return user ? <Navigate to="/" replace /> : <Outlet />;
};

const WelcomeGate = () => {
    const location = useLocation();
    const { user } = useAuth();
    const welcomePath = createPageUrl(WelcomePage);
    const billingPath = createPageUrl(BillingPage);
    const isWelcomePath = location.pathname === welcomePath;
    const isBillingPath = location.pathname === billingPath;

    if (user?.welcome_screen_completed !== true) {
        return isWelcomePath || isBillingPath ? <Outlet /> : <Navigate to={welcomePath} replace />;
    }

    return <Outlet />;
};

// Create a wrapper component that uses useLocation inside the Router context
function PagesContent() {
    const location = useLocation();
    const currentPage = _getCurrentPage(location.pathname);

    return (
        <Routes>
            <Route element={<PublicRoutes />}>
                <Route path={SignInPage} element={<SignIn />} />
                <Route path={SignUpPage} element={<SignUp />} />
                <Route path={VerifyEmailPage} element={<VerifyEmail />} />
                <Route path={AuthConflictPage} element={<AuthConflict />} />
            </Route>

            <Route path={PrivacyPolicyPage} element={<PrivacyPolicy />} />
            <Route path={TermsOfUsePage} element={<TermsOfUse />} />
            <Route path={AnswerSurveyPage} element={<AnswerSurvey />} />
            <Route path={AnswerTestPage} element={<AnswerTest />} />
            <Route path={SubscribePage} element={<Subscribe />} />

            <Route element={<PrivateRoutes />}>
                <Route element={<WelcomeGate />}>
                    <Route path={WelcomePage} element={<Welcome />} />
                    <Route path={SurveyPreviewPage} element={<SurveyPreview />} />
                    <Route path={CustomSurveyBuilderPage} element={<CustomSurveyBuilder />} />
                    <Route path={ReportTemplatePreviewPage} element={<TemplatePreviewPage />} />
                    <Route path={SurveyResponsePage} element={<SurveyResponseView />} />
                    <Route path={TestResponsePage} element={<TestResponseView />} />
                    <Route path={TestPreviewPage} element={<TestPreview />} />
                    <Route
                        element={
                            <Layout currentPageName={currentPage}>
                                <Outlet />
                            </Layout>
                        }
                    >
                        <Route path="/" element={<Dashboard />} />
                        <Route path={DashboardPage} element={<Dashboard />} />
                        <Route path={ProfilePage} element={<Profile />} />
                        <Route path={PatientsPage} element={<Patients />} />
                        <Route path={PatientDetailPage} element={<PatientDetails />} />
                        <Route path={PatientAnamnesisPage} element={<AnamnesisEditorPage />} />
                        <Route path={ReportPage} element={<CreateReport />} />
                        <Route path={BillingPage} element={<Billing />} />
                        <Route path={SurveysPage} element={<Surveys />} />
                        <Route path={AssetsPage} element={<Assets />} />
                        <Route path={ToolsPageRoute} element={<ToolsPage />} />
                        <Route path={HelpPage} element={<Help />} />
                        <Route path={HelpTopicPage} element={<HelpTopic />} />
                        <Route path={HelpArticlePage} element={<HelpArticle />} />
                        <Route path={SupportPage} element={<Support />} />
                        <Route path={SupportInboxPage} element={<SupportInbox />} />
                        <Route path={SupportTicketPage} element={<SupportTicket />} />
                        <Route path={FeatureFlagsAdminPage} element={<FeatureFlags />} />
                        {import.meta.env.DEV ? (
                            <Route
                                path={DEV_RECORDING_SESSION_PAGE}
                                element={<RecordingSessionDevRoute />}
                            />
                        ) : null}
                    </Route>
                </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function Pages() {
    return (
        <Router>
            <PageTracker />
            <AnalyticsIdentity />
            <PagesContent />
            <MobileDesktopRecommendation />
        </Router>
    );
}
